import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
  useEventCallback,
} from '@mui/material';
import { styled } from '@mui/system';
import { TreeView } from '@mui/x-tree-view';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedTypography } from '../extended-typography';
import { ChevronDown, ChevronRight } from '../icons';
import { TextField, TextFieldProps } from '../inputs';
import { SelectedTreeLeaves } from './selected-tree-leaves';
import { TreeViewItem } from './tree-view-item';
import { bfsSearch, getAllChildren, getAllParents } from './utils';

export type TreeViewSelectorProps = {
  value: string[];
  onChange: (value: string[]) => void;
  pendingChanges: string[];
  onChangePendingChanges: (values: string[]) => void;
  searchTerm: string;
  data: TreeViewNode[];
  TextFieldProps: TextFieldProps;
  noOptionsText?: string;
  isLoading?: boolean;
  searchComponent?: React.ReactNode;
  onClear?: () => void;
  readOnly?: boolean;
  treeLeavesDataTestId?: string;
};

export type TreeViewNode = {
  id: string;
  name: string;
  parentId: string | null;
  children: TreeViewNode[];
};

export type TreeViewNodeWithParentData = TreeViewNode & {
  parent?: TreeViewNode;
};

export type TreeNodeWithIndeterminate = Omit<TreeViewNode, 'children'> & {
  indeterminate: boolean;
  children: TreeNodeWithIndeterminate[];
};

const StyledPaper = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(2)};
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    cursor: pointer;

    input {
      cursor: pointer;
    }
  }
`;

export function TreeViewSelector({
  value = [],
  onChange,
  pendingChanges,
  onChangePendingChanges,
  searchTerm,
  data,
  noOptionsText,
  isLoading,
  searchComponent,
  onClear,
  TextFieldProps,
  readOnly,
  treeLeavesDataTestId,
}: TreeViewSelectorProps) {
  const { t } = useTranslation();
  const semantic = useAtlasSemanticThemeTokens();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);

  const handleClear = () => {
    onChangePendingChanges([]);
    onClear?.();
  };

  const handleApply = () => {
    onChange(selectedPendingChangesLeaves.map((node) => node.id));
    setAnchorEl(null);
  };

  const handleTextFieldClick = (event: React.MouseEvent<HTMLElement>) => {
    if (readOnly) return;
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const selectedLeaves = useCallback(
    (value: string[]) => {
      const nodes: TreeViewNodeWithParentData[] = [];

      const getSelectedLeafNodesRecursively = (
        node: TreeViewNode,
        parent?: TreeViewNode,
      ) => {
        if (!node.children.length) {
          if (value.includes(node.id)) {
            nodes.push({ ...node, parent });
          }
        } else {
          node.children.forEach((child) =>
            getSelectedLeafNodesRecursively(child, node),
          );
        }
      };

      data.forEach((node) => getSelectedLeafNodesRecursively(node));

      return nodes;
    },
    [data],
  );

  const areAllChildrenChecked = useCallback(
    (node: TreeViewNode, list: string[]) => {
      const allChildren = getAllChildren(node.id, data);
      const nodeIdIndex = allChildren.indexOf(node.id);
      allChildren.splice(nodeIdIndex, 1);

      return allChildren.every((nodeId) =>
        pendingChanges.concat(list).includes(nodeId),
      );
    },
    [data, pendingChanges],
  );

  const updateNodeStatus = useEventCallback((nodeId: string) => {
    const allChildren = getAllChildren(nodeId, data);
    const parents = getAllParents(data, nodeId);

    let newSelectedNodes = [];

    if (pendingChanges.includes(nodeId)) {
      // Need to de-check
      newSelectedNodes = pendingChanges.filter(
        (id) => !allChildren.concat(parents).includes(id),
      );
    } else {
      // Need to check
      for (let i = 0; i < parents.length; i += 1) {
        const parentNode = bfsSearch(parents[i], data);
        if (parentNode && areAllChildrenChecked(parentNode, allChildren)) {
          allChildren.push(parents[i]);
        }
      }
      newSelectedNodes = [...pendingChanges, ...allChildren];
    }
    onChangePendingChanges([...new Set(newSelectedNodes)]);
  });

  const selectedValueLeaves = selectedLeaves(value);
  const selectedPendingChangesLeaves = selectedLeaves(pendingChanges);
  const selectedPendingChangesLeavesCount = selectedPendingChangesLeaves.length;

  const textFieldValue = useMemo(
    () =>
      selectedValueLeaves.length
        ? `(${selectedValueLeaves.length}) ${selectedValueLeaves
            .map((item) => item.name)
            .join(', ')}`
        : '',
    [selectedValueLeaves],
  );

  const selectedItemsText = useMemo(() => {
    switch (selectedPendingChangesLeavesCount) {
      case 0:
        return '';
      case 1:
        return `
          ${selectedPendingChangesLeavesCount} ${t(
          'inputs.listBox.itemSelected',
        )}
        `;
      default:
        return `
          ${selectedPendingChangesLeavesCount} ${t(
          'inputs.listBox.itemsSelected',
        )}
        `;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPendingChangesLeavesCount]);

  const handleNodeSelect = useEventCallback(
    (event: SyntheticEvent<Element, Event>, nodeIds: string[]) => {
      const isCheckboxClicked = event.target instanceof HTMLInputElement;
      if (isCheckboxClicked) {
        updateNodeStatus(nodeIds[0]);
      } else {
        const children = getAllChildren(nodeIds[0], data);
        if (children.length > 1) {
          if (expanded.includes(nodeIds[0])) {
            setExpanded((prev) => prev.filter((id) => id !== nodeIds[0]));
          } else {
            setExpanded((prev) => [...prev, nodeIds[0]]);
          }
        }
      }
    },
  );

  const handleClickAway = () => {
    setAnchorEl(null);
    onClear?.();
  };

  const isNodeIndeterminate = useCallback(
    (nodeId: string) => {
      const allChildren = getAllChildren(nodeId, data);
      const nodeIdIndex = allChildren.indexOf(nodeId);
      allChildren.splice(nodeIdIndex, 1);

      const selectedChildrenCount = allChildren.filter((nodeId) =>
        pendingChanges.includes(nodeId),
      ).length;

      return (
        selectedChildrenCount > 0 && selectedChildrenCount < allChildren.length
      );
    },
    [data, pendingChanges],
  );

  const getCalculatedItems = useCallback(
    (values: TreeViewNode[]): TreeNodeWithIndeterminate[] =>
      values.map((node) => ({
        ...node,
        indeterminate: isNodeIndeterminate(node.id),
        children: getCalculatedItems(node.children),
      })),
    [isNodeIndeterminate],
  );

  const handleLeafDelete = (id: string) => {
    const newPendingChanges = selectedPendingChangesLeaves.filter(
      (node) => node.id !== id,
    );
    onChange(newPendingChanges.map((node) => node.id));
  };

  const items = getCalculatedItems(data);

  const updateTreeFromValue = useCallback(() => {
    const updatedPendingChanges = value.reduce<string[]>((acc, nodeId) => {
      const allChildren = getAllChildren(nodeId, data);
      const parents = getAllParents(data, nodeId);

      const allChildrenSelected = allChildren.every((child) =>
        value.includes(child),
      );

      if (allChildrenSelected) {
        // Add all children and their parents
        return [...acc, ...allChildren, ...parents];
      } else {
        // Add only the selected node
        return [...acc, nodeId];
      }
    }, []);

    onChangePendingChanges([...new Set(updatedPendingChanges)]);
  }, [data, value, onChangePendingChanges]);

  useEffect(() => {
    if (!searchTerm.length) {
      updateTreeFromValue();
    }
  }, [updateTreeFromValue, searchTerm]);

  return (
    <>
      <StyledTextField
        {...TextFieldProps}
        variant='outlined'
        onClick={handleTextFieldClick}
        value={textFieldValue}
        InputProps={{ endAdornment: <ChevronDown /> }}
        inputProps={{ readOnly }}
      />
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement='bottom-start'
        style={{ width: anchorEl?.clientWidth, zIndex: 1200 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <StyledPaper>
            {searchComponent}
            <Box mt={2}>
              {isLoading || data.length === 0 ? (
                <Box textAlign='center'>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Typography>{noOptionsText}</Typography>
                  )}
                </Box>
              ) : (
                <TreeView
                  multiSelect
                  defaultCollapseIcon={<ChevronDown />}
                  defaultExpandIcon={<ChevronRight />}
                  selected={pendingChanges}
                  expanded={expanded}
                  onNodeSelect={handleNodeSelect}
                >
                  {items.map((node) => (
                    <TreeViewItem
                      key={node.id}
                      node={node}
                      isFirstLevelParent
                    />
                  ))}
                </TreeView>
              )}
            </Box>
            <Stack
              direction='row'
              justifyContent='space-between'
              padding={1}
              paddingTop={2}
              alignItems='center'
            >
              <Button
                variant='outlined'
                data-testid='clear-button'
                onClick={handleClear}
                disabled={isLoading}
              >
                <ExtendedTypography variant='labelSm'>
                  {t('inputs.listBox.clear')}
                </ExtendedTypography>
              </Button>
              <ExtendedTypography
                variant='labelSm'
                fontWeight='bold'
                color={semantic.color.link.default.value}
              >
                {selectedItemsText}
              </ExtendedTypography>
              <Button
                variant='contained'
                data-testid='apply-button'
                onClick={handleApply}
                disabled={isLoading}
              >
                <ExtendedTypography variant='labelSm'>
                  {t('inputs.listBox.apply')}
                </ExtendedTypography>
              </Button>
            </Stack>
          </StyledPaper>
        </ClickAwayListener>
      </Popper>

      <SelectedTreeLeaves
        selectedLeaves={selectedValueLeaves}
        onDelete={handleLeafDelete}
        readOnly={readOnly}
        treeLeavesProps={{ 'data-testid': treeLeavesDataTestId }}
      />
    </>
  );
}
